import { convertFeetToUnits } from '../../commonUtils';

/**
 * Calculates the count per plant based on the given parameters.
 *
 * @param count - The total estimated count of items (berries, clusters, etc...)
 * @param occlusionFactor - The factor by which the count is occluded. If 50% visibility, occlusion factor is 2
 * @param plantSpacingFeet - The spacing between plants in feet.
 * @param distanceTraveled - The distance traveled.
 * @param distanceTraveledUnits - The units of the distance traveled.  Either 'Feet' or 'Meters'.  If 'Feet', the distance will be converted to meters.  Pay close attention here because sometimes the distance is in feet and sometimes it is in meters, regardless of the currently selected units.  For example, it comes in as meters from cube at the ranch summary level, but at the block detail level in scan data, we may already have converted it to feet.
 * @returns The calculated count per plant.
 */
export default function calculateCountPerPlant(
  count: number | null | undefined,
  occlusionFactor: number,
  plantSpacingFeet: number | null | undefined,
  distanceTraveled: number | null | undefined,
  distanceTraveledUnits: 'Feet' | 'Meters'
): number | null {
  if (
    count == null ||
    occlusionFactor == null ||
    plantSpacingFeet == null ||
    distanceTraveled == null ||
    distanceTraveled === 0
  ) {
    return null;
  }

  const plantSpacingMeters = convertFeetToUnits(
    plantSpacingFeet,
    false,
    'Meters'
  );

  let distanceTraveledMeters = distanceTraveled;
  if (distanceTraveledUnits === 'Feet') {
    const distanceConverted = convertFeetToUnits(
      distanceTraveled,
      false,
      'Meters'
    );

    if (!distanceConverted) {
      return null;
    }

    distanceTraveledMeters = distanceConverted;
  }

  if (!plantSpacingMeters) {
    return null;
  }

  const countPerPlant =
    (count * occlusionFactor * plantSpacingMeters) / distanceTraveledMeters;

  return countPerPlant;
}
